import { useCallback, useMemo } from 'react';
import { serialiseFilters, type ReportFilterRow } from './reportFilters';
import type { ReportFilters } from './useReportFilters';

/**
 * Binds the report toolbar to the URL-synced [ReportFilters].
 *
 * Search and Filters live in the query string (and so in react-query's
 * key), which means a filtered view is a shareable link and a refetch
 * happens automatically when either changes — no page needs its own
 * effect to keep them in step.
 */
export interface UseReportToolbar {
  search: string;
  setSearch: (next: string) => void;
  /** The applied filter rows, parsed back from the URL. */
  filterRows: ReportFilterRow[];
  matchAny: boolean;
  applyFilters: (rows: ReportFilterRow[], matchAny: boolean) => void;
  /** True while filter rows are driving the list instead of the date range. */
  filterActive: boolean;
  /** Date pick — clears any applied filter, which otherwise still governs. */
  setRange: (range: { from: string; to: string }) => void;
}

function parseRows(serialised: string): ReportFilterRow[] {
  if (serialised === '') return [];
  try {
    const parsed: unknown = JSON.parse(serialised);
    return Array.isArray(parsed) ? (parsed as ReportFilterRow[]) : [];
  } catch {
    // A hand-mangled URL must not break the page — treat it as no filter.
    return [];
  }
}

export function useReportToolbar(
  filters: ReportFilters,
  update: (next: Partial<ReportFilters>) => void,
  onChanged?: () => void,
): UseReportToolbar {
  const filterRows = useMemo(() => parseRows(filters.filters), [filters.filters]);
  const filterActive = filterRows.length > 0;

  const setSearch = useCallback(
    (next: string) => {
      onChanged?.();
      update({ search: next });
    },
    [onChanged, update],
  );

  const applyFilters = useCallback(
    (rows: ReportFilterRow[], matchAny: boolean) => {
      onChanged?.();
      update({
        filters: serialiseFilters(rows) ?? '',
        // Only carried when it differs from the API default (match-any),
        // so the common case leaves the URL clean.
        matchAny: matchAny ? '' : 'false',
      });
    },
    [onChanged, update],
  );

  const setRange = useCallback(
    (range: { from: string; to: string }) => {
      onChanged?.();
      // A date pick always wins over an applied filter — otherwise the
      // range would appear to change while the filter still governed what
      // came back.
      update({ ...range, filters: '', matchAny: '' });
    },
    [onChanged, update],
  );

  return {
    search: filters.search,
    setSearch,
    filterRows,
    matchAny: filters.matchAny !== 'false',
    applyFilters,
    filterActive,
    setRange,
  };
}
